/**
 * VehicleDetail Page
 * Shows vehicle information and service history
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Car, User, Calendar, Wrench, AlertCircle, RefreshCw, Pencil, History, ExternalLink } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  StatusBadge,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui';
import { ServiceHistoryList } from '../../components/service-history';
import { CustomerActivityTimeline } from '../../components/customers';
import { EntityAuditLog } from '../../components/audit';
import { vehiclesApi, type VehicleWithOwner } from '../../services/vehiclesApi';
import { api } from '../../services/api';
import { useVehicleActivity } from '../../hooks/useVehicles';
import { Modal } from '../../components/ui/modal';
import { VehicleForm } from '../../components/forms/VehicleForm';
import type { UnifiedCaseType } from '@leal-crm/shared';

/**
 * Map booking type to unified case type for workflow lookup
 */
function getBookingType(type?: string): UnifiedCaseType {
  switch (type) {
    case 'seguro':
      return 'insurance';
    case 'garantia':
      return 'warranty';
    default:
      return 'private';
  }
}

interface VehicleBooking {
  id: string;
  caseId?: string;
  scheduledAt: number;
  status: string;
  type?: string;
  notes?: string;
}

export function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [vehicle, setVehicle] = useState<VehicleWithOwner | null>(null);
  const [bookings, setBookings] = useState<VehicleBooking[]>([]);
  const [cases, setCases] = useState<Array<{ id: string; status: string; updatedAt: number; archivedAt: number | null }>>([]);
  const [loading, setLoading] = useState(true);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingCases, setLoadingCases] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { data: activity = [], isLoading: isActivityLoading } = useVehicleActivity(id);

  const loadVehicle = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await vehiclesApi.getById(id);
      setVehicle(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar vehiculo');
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    if (!id) return;

    try {
      setLoadingBookings(true);
      const data = await api.get<{ items: VehicleBooking[] }>(`/api/bookings?vehicleId=${id}&limit=50`);
      setBookings(data.items || []);
    } catch {
      // Silently fail - bookings might not be available
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  const loadCases = async () => {
    if (!id) return;
    try {
      setLoadingCases(true);
      const res = await api.get<{ success: boolean; data: { items: Array<{ case: { id: string; status: string; updatedAt: number; archivedAt: number | null } }> } }>(
        `/api/cases?status=all&vehicleId=${id}&limit=100`
      );
      setCases(res.data.items.map((item) => ({
        id: item.case.id,
        status: item.case.status,
        updatedAt: item.case.updatedAt,
        archivedAt: item.case.archivedAt,
      })) || []);
    } catch {
      setCases([]);
    } finally {
      setLoadingCases(false);
    }
  };

  useEffect(() => {
    loadVehicle();
    loadBookings();
    loadCases();
  }, [id]);

  if (loading) {
    return <VehicleDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link
          to="/vehicles"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver a vehiculos
        </Link>
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-red-300 bg-red-50 p-12 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error al cargar vehiculo</h3>
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <Button onClick={loadVehicle} variant="secondary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">Vehiculo no encontrado</p>
        <Link to="/vehicles" className="mt-4 text-primary hover:underline">
          Volver a vehiculos
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Link */}
      {vehicle.customer && (
        <Link
          to={`/customers/${vehicle.customerId}`}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver a {vehicle.customer.name}
        </Link>
      )}

      {/* Vehicle Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Car className="h-8 w-8 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">
                {vehicle.make} {vehicle.model}
              </h1>
              <Badge variant="outline">{vehicle.registration}</Badge>
            </div>
            <p className="text-muted-foreground">
              {vehicle.year && `${vehicle.year}`}
              {vehicle.year && vehicle.color && ' - '}
              {vehicle.color && vehicle.color}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
          <Pencil className="mr-2 h-4 w-4" />
          Editar
        </Button>
      </div>

      {/* Vehicle Info Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Owner Card */}
        {vehicle.customer && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Propietario
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <Link
                    to={`/customers/${vehicle.customerId}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {vehicle.customer.name}
                  </Link>
                  <p className="text-sm text-muted-foreground">{vehicle.customer.phone}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicle Details Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Detalles del Vehiculo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Placa:</span>
                <span className="font-medium">{vehicle.registration}</span>
              </div>
              {vehicle.vin && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">VIN:</span>
                  <span className="font-mono text-xs">{vehicle.vin}</span>
                </div>
              )}
              {vehicle.insuranceCompany && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aseguradora:</span>
                  <span className="font-medium">{vehicle.insuranceCompany}</span>
                </div>
              )}
              {vehicle.insurancePolicy && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Poliza:</span>
                  <span className="font-medium">{vehicle.insurancePolicy}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
        <Tabs defaultValue="history">
          <TabsList>
            <TabsTrigger value="history" className="gap-2">
              <Wrench className="h-4 w-4" />
              Historial de Servicio
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2">
              <History className="h-4 w-4" />
              Actividad
            </TabsTrigger>
            <TabsTrigger value="cases" className="gap-2">
              <History className="h-4 w-4" />
              Casos
            </TabsTrigger>
            <TabsTrigger value="bookings" className="gap-2">
              <Calendar className="h-4 w-4" />
              Citas
            </TabsTrigger>
            <TabsTrigger value="audit" className="gap-2">
              <History className="h-4 w-4" />
              Cambios
            </TabsTrigger>
          </TabsList>

        <TabsContent value="history" className="mt-4">
          <ServiceHistoryList vehicleId={vehicle.id} vehiclePlate={vehicle.registration} />
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <CustomerActivityTimeline activities={activity} isLoading={isActivityLoading} />
        </TabsContent>

        <TabsContent value="cases" className="mt-4">
          {loadingCases ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : cases.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No hay casos registrados para este vehiculo
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {cases.map((item) => (
                <Card key={item.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <History className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold font-mono">{item.id.slice(0, 8)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Actualizado: {new Date(item.updatedAt * 1000).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={item.archivedAt ? 'CLOSED' : item.status} bookingType="private" />
                      <Link to={`/cases/${item.id}`}>
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bookings" className="mt-4">
          {loadingBookings ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : bookings.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No hay citas registradas para este vehiculo
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {bookings.map((booking) => {
                const date = new Date(booking.scheduledAt * 1000);
                return (
                  <Card key={booking.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                            <Calendar className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {date.toLocaleDateString('es-ES', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {date.toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                              {booking.type && ` - ${booking.type}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <StatusBadge status={booking.status} bookingType={getBookingType(booking.type)} />
                          <Link to={booking.caseId ? `/cases/${booking.caseId}` : `/bookings/${booking.id}`}>
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <EntityAuditLog
            entityType="vehicle"
            entityId={vehicle.id}
            title="Historial de cambios del vehiculo"
          />
        </TabsContent>
      </Tabs>

      {/* Edit Vehicle Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Editar vehículo" size="md">
        <VehicleForm
          customerId={vehicle.customerId}
          vehicleId={vehicle.id}
          initialData={{
            registration: vehicle.registration,
            make: vehicle.make,
            model: vehicle.model,
            year: vehicle.year ? String(vehicle.year) : '',
            color: vehicle.color ?? '',
          }}
          onSuccess={() => {
            setIsEditModalOpen(false);
            loadVehicle();
          }}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

function VehicleDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-4 w-32" />
      <div className="flex items-start gap-4">
        <Skeleton className="h-16 w-16 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-4 w-20 mb-4" />
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-4 w-32 mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
